import React, { useState, useEffect, useCallback, useContext } from 'react';
import apiConfig from '../config/apiConfig';
import { useAppState } from '../context/AppStateContext';
import CooldownTimer from '../components/CooldownTimer';
import { UserContext } from '../context/UserContext';
import './Deeds.css';

// Local storage key for cooldown tracking
const COOLDOWN_STORAGE_KEY = 'legacy_reborn_deeds_cooldown';
// Local storage keys for user preferences
const ALIGNMENT_FILTER_STORAGE_KEY = 'legacy_reborn_deeds_alignment_filter';
const LEVEL_SORT_STORAGE_KEY = 'legacy_reborn_deeds_level_sort';

const Deeds = () => {
  // User context for admin checks
  const { user } = useContext(UserContext);
  
  // Basic state
  const [deeds, setDeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attemptingDeed, setAttemptingDeed] = useState(false);
  
  // Deed result display state
  const [deedResult, setDeedResult] = useState(null);
  const [displayMode, setDisplayMode] = useState('normal'); // 'normal', 'success', 'failure', 'cooldown', 'jail'
  
  // UI state for expanded categories
  const [expandedCategories, setExpandedCategories] = useState({
    positive: false,
    neutral: false,
    negative: false
  });
  
  // Filter and sort preferences (initialized from localStorage)
  const [alignmentFilter, setAlignmentFilter] = useState(() => {
    const savedFilter = localStorage.getItem(ALIGNMENT_FILTER_STORAGE_KEY);
    return savedFilter || 'all';
  });
  
  const [levelSort, setLevelSort] = useState(() => {
    const savedSort = localStorage.getItem(LEVEL_SORT_STORAGE_KEY);
    return savedSort || 'desc';
  });
  
  // Get app state for cooldowns and jail status
  const appState = useAppState();
  const { deedsCooldown, updateDeedsCooldown, updateJailStatus } = appState;
  const jailStatus = appState.jailStatus;

  // Add a new state for the selected deed (before attempting)
  const [selectedDeed, setSelectedDeed] = useState(null);

  // Add a flag to track if deeds have been loaded
  const [deedsLoaded, setDeedsLoaded] = useState(false);

  // Add a state variable for 'inJail'
  const [inJail, setInJail] = useState(false);
  
  // Update setInJail when jail status changes
  useEffect(() => {
    // This effect will make sure inJail state is updated when jail status changes
    setInJail(jailStatus?.isJailed || false);
  }, [jailStatus]);

  // Function to fetch available deeds
  const fetchDeeds = useCallback(async () => {
    // Added console log for debugging
    console.log('fetchDeeds called. Loading:', loading, 'inJail:', inJail);
    
    // Don't fetch if already loading
    if (loading && deedsLoaded) {
      console.log('Already loading or deeds loaded, skipping fetch');
      return;
    }
    
    // Check both cooldown and jail status before fetching
    if (inJail) {
      console.log('User is in jail, skipping deed fetch');
      setLoading(false);
      return;
    }
    
    // Check for active cooldown
    if (deedsCooldown) {
      const cooldownTime = new Date(deedsCooldown);
      if (cooldownTime > Date.now()) {
        console.log('Deeds on cooldown, skipping fetch');
        // Make sure the cooldown is set in app state
        updateDeedsCooldown(cooldownTime);
        
        setDeedResult({
          success: false,
          isOnCooldown: true,
          message: 'You must wait for the cooldown to expire before performing more deeds.',
          cooldown: cooldownTime,
          inJail: false
        });
        setLoading(false);
        return;
      }
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(apiConfig.deeds, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch deeds');
      }

      const data = await response.json();
      console.log(`Fetched ${data.length} deeds`);
      setDeeds(data);
      setDeedsLoaded(true);
      
      // Clear any cooldown if we have available deeds
      const anyAvailable = data.some(deed => !deed.onCooldown);
      if (anyAvailable) {
        updateDeedsCooldown(null);
      }
    } catch (err) {
      console.error('Error fetching deeds:', err);
      setError('Failed to load deeds. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [deedsCooldown, inJail, updateDeedsCooldown, loading, deedsLoaded]);

  // Initial fetch on component mount - only fetch once
  useEffect(() => {
    // Add a check if deeds are already loaded
    if (deeds.length > 0) {
      console.log('Deeds already loaded, skipping fetch');
      return;
    }
    
    if (!deedsLoaded && !deedResult) {
      console.log('Initial fetch effect running - first load');
      fetchDeeds();
    } else {
      console.log('Skipping initial fetch - deeds already loaded or result exists');
    }
  }, [fetchDeeds, deedsLoaded, deedResult, deeds.length]);

  // Function to select a deed (step 1 of the 2-step process)
  const selectDeed = (deed) => {
    setSelectedDeed(deed);
  };

  // Function to clear selected deed and return to list
  const clearSelectedDeed = () => {
    setSelectedDeed(null);
  };

  // Function to attempt a deed (step 2 of the 2-step process)
  const attemptDeed = async (deedId) => {
    try {
      // Find the deed in the list but only use it if needed
      const selectedDeedObj = deeds.find(deed => deed.id === deedId);
      
      setAttemptingDeed(true);
      setError(null);
      setDeedResult(null);
      
      // Call the API to attempt the deed
      const response = await fetch(apiConfig.completeDeed(deedId), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Parse the response, which contains just message, cooldown, and inJail
      const data = await response.json();
      
      // More verbose logging to debug the issue
      console.log('★★★ DEED ATTEMPT RESPONSE ★★★');
      console.log('Raw response:', data);
      console.log('Success value:', data.success);
      console.log('Message:', data.message || 'NO MESSAGE RECEIVED');
      console.log('Cooldown:', data.cooldown ? new Date(data.cooldown).toLocaleString() : 'NO COOLDOWN');
      console.log('Last Jail:', data.lastJail ? new Date(data.lastJail).toLocaleString() : 'NOT JAILED');
      
      // Make sure we have a message
      if (!data.message || data.message.trim() === '') {
        console.warn('API returned empty message, using fallback');
        data.message = 'The deed was completed, but no details were provided.';
      }
      
      // Determine display mode based on new success value
      let newDisplayMode = 'success'; // Default to success
      
      // Based on the new API response format
      if (data.success === -1) {
        newDisplayMode = 'cooldown';
      } else if (data.success === 0) {
        newDisplayMode = 'failure';
      } else if (data.success === 1) {
        newDisplayMode = 'success';
      }
      
      // Check for jail status
      const isJailed = data.lastJail && new Date(data.lastJail) > new Date();
      if (isJailed) {
        newDisplayMode = 'jail';
      }
      
      // Handle cooldown if present
      if (data.cooldown) {
        const cooldownTime = new Date(data.cooldown);
        console.log('Setting deed cooldown in app state:', cooldownTime);
        
        // Set the cooldown state first, then update localStorage
        // This order prevents multiple state updates
        updateDeedsCooldown(cooldownTime);
        
        // Use a single localStorage update
        localStorage.setItem(COOLDOWN_STORAGE_KEY, cooldownTime.toISOString());
      }
      
      // Update jail status if player is jailed - but only once
      if (isJailed) {
        updateJailStatus(true, data.lastJail);
      }
      
      // Create the result object with the API response - ensure message is preserved
      const finalDeedResult = {
        success: data.success === 1,  // Convert numeric to boolean for existing code
        isOnCooldown: data.success === -1,
        message: data.message,
        apiMessage: data.message,
        rawApiResponse: JSON.stringify(data, null, 2), // Store raw response for debugging
        deedName: selectedDeed?.name || selectedDeedObj?.name || 'Deed',
        inJail: isJailed,
        cooldown: data.cooldown ? new Date(data.cooldown) : null,
        lastJail: data.lastJail ? new Date(data.lastJail) : null,
        cooldownMessage: 'You must wait for the cooldown to expire before performing more deeds.'
      };
      
      console.log('Setting deed result with message:', finalDeedResult.message);
      
      // Clear selected deed since we've now attempted it
      setSelectedDeed(null);
      
      // Update state with result - IMPORTANT: set displayMode first, then deedResult
      setDisplayMode(newDisplayMode);
      
      // Small timeout to ensure state updates properly - use a single timeout
      setTimeout(() => {
        setDeedResult(finalDeedResult);
        
        // Set flag for deeds needing refresh, but don't trigger the fetch yet
        setDeedsLoaded(false);
        
        // Log after setting the result to verify it was set correctly
        console.log('Deed result set in state:', finalDeedResult.message);
        
        // INCREASE the timeout before transitioning to cooldown to give user time to read the message
        if (data.cooldown && !isJailed && newDisplayMode !== 'cooldown') {
          // We'll set a timeout for the cooldown transition, but no additional fetches
          setTimeout(() => {
            console.log('Transitioning to cooldown, preserving message:', finalDeedResult.apiMessage);
            
            // Batch these state updates to reduce re-renders
            setDisplayMode('cooldown');
            setDeedResult(prev => ({
              ...prev,
              isOnCooldown: true,
              // Explicitly keep the original message
              message: data.message
            }));
          }, 15000); // Increased from 7000 to 15000 (15 seconds)
        }
      }, 10);
      
      // No more setTimeout calls here - we've consolidated them above
    } catch (err) {
      console.error('Error attempting deed:', err);
      setError(err.message || 'Failed to attempt deed. Please try again.');
    } finally {
      setAttemptingDeed(false);
    }
  };

  // Format cooldown time for display
  const formatTimeRemaining = (cooldownEnd) => {
    const now = new Date();
    const endTime = new Date(cooldownEnd);
    const diffMs = endTime - now;
    
    if (diffMs <= 0) return 'Ready';
    
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);
    
    if (diffMins > 0) {
      return `${diffMins}m ${diffSecs}s`;
    } else {
      return `${diffSecs}s`;
    }
  };

  // Helper functions for UI
  const groupDeedsByAlignment = () => {
    return deeds.reduce(
      (acc, deed) => {
        if (deed.alignment > 0) {
          acc.positive.push(deed);
        } else if (deed.alignment < 0) {
          acc.negative.push(deed);
        } else {
          acc.neutral.push(deed);
        }
        return acc;
      },
      { positive: [], neutral: [], negative: [] }
    );
  };

  // Sort grouped deeds by level
  const sortGroupedDeeds = (grouped) => {
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => levelSort === 'desc' ? b.level - a.level : a.level - b.level);
    });
    return grouped;
  };

  // Get filtered categories based on user selection
  const getFilteredCategories = (grouped) => {
    if (alignmentFilter === 'all') {
      return Object.keys(grouped);
    } else {
      return [alignmentFilter];
    }
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Get visible deeds (all or top 3)
  const getVisibleDeeds = (category, grouped) => {
    return expandedCategories[category] 
      ? grouped[category] 
      : grouped[category].slice(0, 3);
  };

  // Get alignment display name
  const getAlignmentName = (category) => {
    switch (category) {
      case 'positive': return 'Good';
      case 'neutral': return 'Neutral';
      case 'negative': return 'Evil';
      default: return category;
    }
  };

  // Get alignment emoji
  const getAlignmentEmoji = (category) => {
    switch (category) {
      case 'positive': return '😇';
      case 'neutral': return '😐';
      case 'negative': return '😈';
      default: return '';
    }
  };

  // Update alignment filter with localStorage persistence
  const updateAlignmentFilter = (filter) => {
    localStorage.setItem(ALIGNMENT_FILTER_STORAGE_KEY, filter);
    setAlignmentFilter(filter);
  };
  
  // Update level sort with localStorage persistence
  const updateLevelSort = (sort) => {
    localStorage.setItem(LEVEL_SORT_STORAGE_KEY, sort);
    setLevelSort(sort);
  };

  // Close deed result and return to deeds list - only fetch if needed
  const closeDeedResult = () => {
    setDeedResult(null);
    
    // Only fetch deeds if we don't have them already or they're stale
    const needsFreshDeeds = deeds.length === 0 || !deedsLoaded;
    
    // Check if we need to fetch fresh deeds (only if not on cooldown)
    if (deedsCooldown) {
      const cooldownTime = new Date(deedsCooldown);
      console.log('Current cooldown time when closing result:', cooldownTime);
      
      if (cooldownTime <= Date.now()) {
        // Cooldown expired, safe to fetch and clear cooldown
        console.log('Cooldown expired, clearing cooldown');
        updateDeedsCooldown(null);
        
        if (needsFreshDeeds) {
          console.log('Fetching fresh deeds after cooldown expired');
          setDeedsLoaded(false);
          fetchDeeds();
        }
      } else {
        // Still on cooldown, make sure it's set properly in app state
        console.log('Still on cooldown, ensuring it is set in app state');
        updateDeedsCooldown(cooldownTime);
      }
    } else if (needsFreshDeeds) {
      // No cooldown and we need fresh deeds
      console.log('No cooldown, fetching fresh deeds');
      setDeedsLoaded(false);
      fetchDeeds();
    }
  };

  // Add an effect to ensure cooldown is properly synchronized with app state
  // Run this effect only once on component mount
  useEffect(() => {
    // Check if there's a cooldown in localStorage that needs to be restored
    const storedCooldown = localStorage.getItem(COOLDOWN_STORAGE_KEY);
    if (storedCooldown) {
      try {
        // Parse using ISO date string format instead of parseInt
        const cooldownTime = new Date(storedCooldown);
        
        // Only set if it's still in the future
        if (cooldownTime > Date.now()) {
          console.log('Restoring cooldown from localStorage:', cooldownTime);
          updateDeedsCooldown(cooldownTime);
        } else {
          // Cooldown has expired, clear it
          console.log('Stored cooldown has expired, clearing');
          localStorage.removeItem(COOLDOWN_STORAGE_KEY);
          updateDeedsCooldown(null);
        }
      } catch (e) {
        console.error('Error parsing stored cooldown:', e);
        localStorage.removeItem(COOLDOWN_STORAGE_KEY);
      }
    }
    // This effect should only run ONCE on mount, so we intentionally exclude updateDeedsCooldown
    // to prevent re-runs, but we need to tell ESLint this is intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  // Create a separate effect to update the cooldown in app state if needed, but with
  // a strict check to prevent unnecessary updates
  useEffect(() => {
    // Only do this update if needed - check actual time values
    const storedCooldown = localStorage.getItem(COOLDOWN_STORAGE_KEY);
    if (storedCooldown && deedsCooldown) {
      try {
        const storedTime = new Date(storedCooldown).getTime();
        const currentCooldownTime = deedsCooldown.getTime();
        
        // Only update if times are significantly different (more than 1 second)
        if (Math.abs(storedTime - currentCooldownTime) > 1000) {
          console.log('Syncing cooldown with localStorage due to significant difference');
          updateDeedsCooldown(new Date(storedCooldown));
        }
      } catch (e) {
        console.error('Error comparing cooldown times:', e);
      }
    }
  }, [deedsCooldown, updateDeedsCooldown]);
  
  // Process deeds for display
  const groupedDeeds = sortGroupedDeeds(groupDeedsByAlignment());
  const filteredCategories = getFilteredCategories(groupedDeeds);

  return (
    <div className="deeds-container">
      <h1>Deeds</h1>

      {/* Error message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="dismiss-button">Dismiss</button>
        </div>
      )}

      {/* Loading overlay */}
      {attemptingDeed && (
        <div className="attempting-deed-overlay">
          <div className="attempting-deed">
            <div className="spinner"></div>
            <p>Attempting deed...</p>
          </div>
        </div>
      )}

      {/* Deed Confirmation Screen (new) */}
      {selectedDeed && !deedResult ? (
        <div className="deed-confirmation-page">
          <div className="deed-confirmation-container">
            <h2>Confirm Deed Attempt</h2>
            
            <div className="deed-confirmation-details">
              <h3>{selectedDeed.name}</h3>
              <div className="deed-confirmation-level">Level {selectedDeed.level}</div>
              <p className="deed-confirmation-content">{selectedDeed.content}</p>
              
              <div className="deed-confirmation-alignment">
                <span className="alignment-label">Alignment:</span>
                <span className={`alignment-value ${
                  selectedDeed.alignment > 0 ? 'good' : 
                  selectedDeed.alignment < 0 ? 'evil' : 'neutral'
                }`}>
                  {selectedDeed.alignment > 0 ? 'Good' : 
                   selectedDeed.alignment < 0 ? 'Evil' : 'Neutral'}
                </span>
                <span className="alignment-emoji">
                  {selectedDeed.alignment > 0 ? '😇' : 
                   selectedDeed.alignment < 0 ? '😈' : '😐'}
                </span>
              </div>
              
              <div className="deed-confirmation-warning">
                <p>Are you sure you want to attempt this deed?</p>
                <p>Be aware that some deeds carry risks and may have consequences.</p>
              </div>
              
              <div className="deed-confirmation-buttons">
                <button 
                  className="attempt-deed-button"
                  onClick={() => attemptDeed(selectedDeed.id)}
                  disabled={attemptingDeed}
                >
                  Attempt Now
                </button>
                <button 
                  className="cancel-deed-button"
                  onClick={clearSelectedDeed}
                  disabled={attemptingDeed}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : deedResult ? (
        // Existing deed result page
        <div className="deed-result-page">
          <div className={`deed-result-container ${displayMode}`}>
            <h2>
              {displayMode === 'jail' ? 'You\'ve Been Jailed!' : 
               displayMode === 'cooldown' ? 'On Cooldown' : 
               displayMode === 'failure' ? `${deedResult.deedName || 'Deed'} Failed!` :
               `${deedResult.deedName || 'Deed'} Completed!`}
            </h2>
            
            {/* More prominently display the API message */}
            <div className="deed-message-container">
              <p className="deed-message deed-api-message">
                {deedResult.message || 'No message received from server'}
              </p>
              
              {/* Only show cooldown message as a secondary message in cooldown mode */}
              {displayMode === 'cooldown' && (
                <p className="cooldown-message-text">{deedResult.cooldownMessage}</p>
              )}
            </div>
            
            {/* Debug info for everyone - temporarily to debug the issue */}
            <div className="debug-info">
              <details>
                <summary>Message Info</summary>
                <div>
                  <p><strong>Current Display Mode:</strong> {displayMode}</p>
                  <p><strong>Success Value:</strong> {deedResult.rawApiResponse ? JSON.parse(deedResult.rawApiResponse).success : 'N/A'}</p>
                  <p><strong>Is on Cooldown:</strong> {deedResult.isOnCooldown ? 'Yes' : 'No'}</p>
                  <p><strong>Raw Message:</strong> "{deedResult.message}"</p>
                </div>
              </details>
            </div>
            
            {/* Debug section for admins */}
            {user?.isAdmin && (
              <div className="admin-debug-section">
                <details>
                  <summary>Debug Info</summary>
                  <div className="debug-content">
                    <h4>API Response:</h4>
                    <pre>{deedResult.rawApiResponse || 'No raw data available'}</pre>
                  </div>
                </details>
              </div>
            )}
            
            {displayMode === 'jail' && (
              <div className="jail-notification">
                <span className="jail-icon">🔒</span>
                <p>You have been imprisoned for your actions!</p>
              </div>
            )}
            
            {deedResult.cooldown && displayMode === 'cooldown' && (
              <div className="deed-cooldown-display">
                <h3>Cooldown Timer</h3>
                <div className="cooldown-progress">
                  <CooldownTimer 
                    targetTime={deedResult.cooldown} 
                    onComplete={() => {
                      console.log('Cooldown completed');
                      closeDeedResult();
                    }}
                  />
                </div>
                <p className="cooldown-message">
                  {deedResult.cooldownMessage}
                </p>
              </div>
            )}
            
            {deedResult.cooldown && (displayMode === 'success' || displayMode === 'failure') && (
              <div className="transition-message">
                <p>Cooldown timer will appear in a few seconds...</p>
              </div>
            )}
            
            {(displayMode !== 'cooldown' || user?.isAdmin) && (
              <button 
                onClick={() => {
                  if (deedResult.cooldown && displayMode !== 'cooldown') {
                    setDisplayMode('cooldown');
                    setDeedResult(prev => ({...prev, isOnCooldown: true}));
                  } else {
                    closeDeedResult();
                  }
                }}
                className="return-to-deeds-button"
              >
                {deedResult.cooldown && displayMode !== 'cooldown' ? "View Cooldown" : "Return to Deeds List"}
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Loading indicator */}
          {loading && (
            <div className="loading">Loading available deeds...</div>
          )}

          {/* Deeds list */}
          {!loading && (
            <>
              {/* Filtering and sorting controls */}
              <div className="deeds-controls">
                <div className="filter-controls">
                  <span className="control-label">Filter by:</span>
                  <div className="filter-buttons">
                    <button 
                      className={`filter-button ${alignmentFilter === 'all' ? 'active' : ''}`}
                      onClick={() => updateAlignmentFilter('all')}
                    >
                      All
                    </button>
                    <button 
                      className={`filter-button ${alignmentFilter === 'positive' ? 'active' : ''}`}
                      onClick={() => updateAlignmentFilter('positive')}
                    >
                      Good <span className="alignment-emoji">😇</span>
                    </button>
                    <button 
                      className={`filter-button ${alignmentFilter === 'neutral' ? 'active' : ''}`}
                      onClick={() => updateAlignmentFilter('neutral')}
                    >
                      Neutral <span className="alignment-emoji">😐</span>
                    </button>
                    <button 
                      className={`filter-button ${alignmentFilter === 'negative' ? 'active' : ''}`}
                      onClick={() => updateAlignmentFilter('negative')}
                    >
                      Evil <span className="alignment-emoji">😈</span>
                    </button>
                  </div>
                </div>
                
                <div className="sort-controls">
                  <span className="control-label">Sort level:</span>
                  <div className="sort-buttons">
                    <button 
                      className={`sort-button ${levelSort === 'desc' ? 'active' : ''}`}
                      onClick={() => updateLevelSort('desc')}
                      title="Sort by highest level first"
                    >
                      Highest First
                    </button>
                    <button 
                      className={`sort-button ${levelSort === 'asc' ? 'active' : ''}`}
                      onClick={() => updateLevelSort('asc')}
                      title="Sort by lowest level first"
                    >
                      Lowest First
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Deed sections by alignment */}
              <div className="deeds-sections">
                {filteredCategories.map(category => (
                  <div key={category} className={`deeds-section ${category}`}>
                    <div className="section-header">
                      <h2>{getAlignmentName(category)}</h2>
                      <span className="alignment-icon">{getAlignmentEmoji(category)}</span>
                    </div>

                    {groupedDeeds[category].length === 0 ? (
                      <div className="no-deeds">No {getAlignmentName(category).toLowerCase()} deeds available at your level.</div>
                    ) : (
                      <>
                        <div className="deeds-grid">
                          {getVisibleDeeds(category, groupedDeeds).map(deed => (
                            <div key={deed.id} className={`deed-card ${deed.onCooldown ? 'on-cooldown' : ''}`}>
                              <div className="deed-header">
                                <h3 className="deed-title">{deed.name}</h3>
                                <span className="deed-level">Level {deed.level}</span>
                              </div>
                              <p className="deed-content">{deed.content}</p>
                              <button 
                                className="deed-action"
                                onClick={() => selectDeed(deed)} // Changed to selectDeed instead of attemptDeed
                                disabled={deed.onCooldown}
                              >
                                {deed.onCooldown ? 'On Cooldown' : 'Attempt Deed'}
                              </button>
                              {deed.onCooldown && deed.cooldownEnd && (
                                <div className="cooldown-timer">
                                  Ready in: {formatTimeRemaining(deed.cooldownEnd)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>

                        {groupedDeeds[category].length > 3 && (
                          <button 
                            className="show-more-button"
                            onClick={() => toggleCategory(category)}
                          >
                            {expandedCategories[category] ? 'Show Less' : `Show ${groupedDeeds[category].length - 3} More`}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Deeds;